import React, { useState, useEffect, useRef } from 'react';
import {  
  Search, Filter, ExternalLink, PenTool, Sparkles, 
  BarChart2, PieChart, Globe, UserCheck, TrendingUp, Link as LinkIcon,
  ChevronDown, ChevronRight, Zap, ListVideo, X
} from 'lucide-react';
import { Bar, Doughnut } from 'react-chartjs-2';
import axios from 'axios';
import { API_URL as CONFIG_API_URL } from '../../config';
const API_URL = CONFIG_API_URL || 'http://8.141.122.155:3002/api';

import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip as ChartTooltip, Legend, ArcElement
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, ArcElement);

const ALL_PLATFORMS = ['百度', '小红书', '抖音', '微博', '知乎', 'B站'];

export default function SeoMediaInsights() {
  const [selectedPlatforms, setSelectedPlatforms] = useState(['百度', '小红书', '抖音']); 
  const [platform, setPlatform] = useState('百度');
  const [viewMode, setViewMode] = useState('media'); // 'media' | 'viral'
  const [viralMetric, setViralMetric] = useState('likes'); // 'likes' | 'comments' | 'shares'
  useEffect(() => { if (platform === '百度' || platform === '知乎') { setViralMetric('score'); } else { setViralMetric('likes'); } }, [platform]);
  const [showPlatformFilter, setShowPlatformFilter] = useState(false);
  const filterRef = useRef(null);

  const isSearchEngine = platform && (platform.includes('百度') || platform.includes('微信') || platform === '头条' || platform === '搜狗' || platform === '360');

  useEffect(() => {
    if (isSearchEngine) {
      if (['likes', 'comments', 'shares', 'favorites'].includes(viralMetric)) setViralMetric('score');
    } else {
      if (['score', 'count'].includes(viralMetric)) setViralMetric('likes');
    }
  }, [platform]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowPlatformFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Make sure active platform is in the selected list
  useEffect(() => {
    if (selectedPlatforms.length > 0 && !selectedPlatforms.includes(platform)) {
      setPlatform(selectedPlatforms[0]);
    }
  }, [selectedPlatforms]);
  
  const togglePlatform = (p) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(p)) {
        if (prev.length === 1) return prev; // Keep at least one
        return prev.filter(item => item !== p);
      } else {
        return [...prev, p];
      }
    });
  };

    const [data, setData] = useState({
    leaderboard: { labels: [], data: [] },
    contentTypes: { labels: [], data: [] },
    evidence: [],
    viralData: { typePerformance: [], themePerformance: [], topViral: [] }
  });
  const [loading, setLoading] = useState(false);
  const [brandProjectId, setBrandProjectId] = useState('');
  const [isLeaderboardModalOpen, setIsLeaderboardModalOpen] = useState(false);

  // 监听全局项目切换
  useEffect(() => {
    const fetchGlobalBrand = () => {
      const bid = localStorage.getItem('globalBrand');
      if (bid && bid !== 'all' && bid !== 'undefined') {
        setBrandProjectId(bid);
      }
    };
    fetchGlobalBrand();
    window.addEventListener('globalFilterChange', fetchGlobalBrand);
    return () => window.removeEventListener('globalFilterChange', fetchGlobalBrand);
  }, []);

  useEffect(() => {
    if (!brandProjectId) return;
    setLoading(true);
    axios.get(`${API_URL}/dashboard/media-insights`, { params: { brandProjectId, platform, metric: viralMetric } })
      .then(res => {
         if (res.data.success && res.data.data) {
             const respData = res.data.data;
             // If there's no data, provide empty fallbacks
             setData({
                 leaderboard: respData.leaderboard?.labels?.length > 0 ? respData.leaderboard : { labels: ['暂无数据'], data: [0] },
                 contentTypes: respData.contentTypes?.labels?.length > 0 ? respData.contentTypes : { labels: ['暂无数据'], data: [0] },
                 evidence: respData.evidence || [],
                 viralData: respData.viralData || { typePerformance: [], themePerformance: [], topViral: [] }
             });
         }
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, [brandProjectId, platform, viralMetric]);

  // Map metric to Chinese label
  const metricLabelMap = {
    'likes': '平均点赞数',
    'comments': '平均评论数',
    'shares': '平均转发数',
    'favorites': '平均收藏数',
    'score': '平均能见度得分',
    'count': '内容收录数'
  };
  const currentMetricLabel = metricLabelMap[viralMetric] || '平均数据';

  // ================= 图表配置 =================
  const barOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `综合能见度得分: ${ctx.raw}`
        }
      }
    },
    scales: {
      x: { 
        grid: { color: 'rgba(148, 163, 184, 0.1)' },
        ticks: { color: '#94a3b8' },
        border: { display: true, color: 'rgba(148, 163, 184, 0.3)' }
      },
      y: { 
        grid: { display: false },
        ticks: { color: '#94a3b8', font: { family: 'Inter, sans-serif' } },
        border: { display: true, color: 'rgba(148, 163, 184, 0.3)' }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { color: '#94a3b8' } }
    },
    cutout: '60%'
  };

  // 跳转智墨
  const handleJumpToZhimo = (title, type) => {
    const currentUserStr = localStorage.getItem('user');
    const currentUserId = currentUserStr ? JSON.parse(currentUserStr).id : 1;
    const zhimoUrl = `https://www.sapientink.com/content/articles?sso_user_id=${currentUserId}&topic=${encodeURIComponent(title)}&style=${encodeURIComponent(type)}`;
    window.open(zhimoUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pb-12">
      <div className="max-w-full px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header & Platform Toggle */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 space-y-4 md:space-y-0 relative" ref={filterRef}>
          <div>
            <div className="flex bg-slate-200 dark:bg-slate-800 rounded-lg p-1 text-sm font-medium shadow-inner w-fit mb-4">
              <button
                onClick={() => setViewMode('media')}
                className={`px-5 py-2 rounded-md transition-all ${
                  viewMode === 'media' 
                    ? 'bg-white dark:bg-slate-700 text-[#2996cf] shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <ListVideo className="w-4 h-4 inline mr-1.5" />
                媒体阵地排名洞察
              </button>
              <button
                onClick={() => setViewMode('viral')}
                className={`px-5 py-2 rounded-md transition-all ${
                  viewMode === 'viral' 
                    ? 'bg-white dark:bg-slate-700 text-[#2996cf] shadow-sm' 
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }`}
              >
                <Zap className="w-4 h-4 inline mr-1.5" />
                爆款基因与内容策略
              </button>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center space-x-4">
              <span>媒体达人高位占位分析</span>
            </h2>
            <p className="text-gray-500 dark:text-slate-400 mt-2">
              分析高位媒体阵地与内容画像，指导品牌发稿与种草策略。
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Active Platform Tabs */}
            {selectedPlatforms.length > 0 && (
              <div className="flex bg-slate-200 dark:bg-slate-800 rounded-lg p-1 text-sm font-medium shadow-inner">
                {selectedPlatforms.map(p => (
                  <button
                    key={p}
                    onClick={() => setPlatform(p)}
                    className={`px-5 py-2 rounded-md transition-all ${
                      platform === p 
                        ? 'bg-white dark:bg-slate-700 text-[#2996cf] shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    {p === '百度' || p === '知乎' || p === '微博' ? <Globe className="w-4 h-4 inline mr-1.5" /> : <UserCheck className="w-4 h-4 inline mr-1.5" />}
                    {p}
                  </button>
                ))}
              </div>
            )}

            {/* Filter Dropdown */}
            <div className="relative">
              <button 
                onClick={() => setShowPlatformFilter(!showPlatformFilter)}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-sm text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
              >
                <Filter className="w-4 h-4" />
                配置分析平台
                <ChevronDown className="w-4 h-4 ml-1" />
              </button>
              
              {showPlatformFilter && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-100 dark:border-slate-700 z-50 p-3">
                  <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 px-1">勾选分析域 (至少保留1项)</div>
                  <div className="space-y-1">
                    {ALL_PLATFORMS.map(p => (
                      <label key={p} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={selectedPlatforms.includes(p)}
                          onChange={() => togglePlatform(p)}
                          className="rounded border-gray-300 text-[#2996cf] focus:ring-[#2996cf]"
                        />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{p}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {!brandProjectId ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500 bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700">
            <Search className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
            请先在顶部选择品牌与项目
          </div>
        ) : selectedPlatforms.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-slate-800/50 rounded-xl border border-gray-200 dark:border-slate-700">
            <Filter className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <div className="text-slate-500">请在右上角「配置分析平台」中勾选至少一个平台</div>
          </div>
        ) : (
          <>
            {viewMode === 'viral' ? (
              <div className={`transition-all duration-300 ${loading ? 'opacity-50 pointer-events-none blur-[2px]' : ''}`}>
                <div className="flex justify-end mb-4">
                  <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1 text-sm shadow-sm border border-gray-200 dark:border-slate-700">
                    {isSearchEngine ? (
                    <>
                      <button
                        onClick={() => setViralMetric('score')}
                        className={`px-4 py-1.5 rounded-md transition-all ${viralMetric === 'score' ? 'bg-white dark:bg-slate-600 text-[#2996cf] shadow-sm font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
                      >
                        按能见度
                      </button>
                      <button
                        onClick={() => setViralMetric('count')}
                        className={`px-4 py-1.5 rounded-md transition-all ${viralMetric === 'count' ? 'bg-white dark:bg-slate-600 text-teal-500 shadow-sm font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
                      >
                        按收录量
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setViralMetric('likes')}
                      className={`px-4 py-1.5 rounded-md transition-all ${viralMetric === 'likes' ? 'bg-white dark:bg-slate-600 text-amber-500 shadow-sm font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
                    >
                      按点赞
                    </button>
                    <button
                      onClick={() => setViralMetric('comments')}
                      className={`px-4 py-1.5 rounded-md transition-all ${viralMetric === 'comments' ? 'bg-white dark:bg-slate-600 text-emerald-500 shadow-sm font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
                    >
                      按评论
                    </button>
                    <button
                      onClick={() => setViralMetric('favorites')}
                      className={`px-4 py-1.5 rounded-md transition-all ${viralMetric === 'favorites' ? 'bg-white dark:bg-slate-600 text-rose-500 shadow-sm font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
                    >
                      按收藏
                    </button>
                    {platform !== '小红书' && (
                    <button
                      onClick={() => setViralMetric('shares')}
                      className={`px-4 py-1.5 rounded-md transition-all ${viralMetric === 'shares' ? 'bg-white dark:bg-slate-600 text-indigo-500 shadow-sm font-medium' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'}`}
                    >
                      按转发
                    </button>
                    )}
                    </>
                  )}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Viral Chart 1: Type Performance */}
                  <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                    <h3 className="text-base font-bold text-gray-900 dark:text-slate-100 flex items-center mb-6">
                      <Zap className="w-5 h-5 mr-2 text-amber-500" />
                      {isSearchEngine ? '各内容流派能见度分布 (霸屏公式)' : '各内容流派互动分布 (最佳公式)'}
                    </h3>
                    <div className="h-[300px]">
                      <Bar 
                        data={{
                          labels: data.viralData.typePerformance.map(t => t.type),
                          datasets: [{
                            label: currentMetricLabel,
                            data: data.viralData.typePerformance.map(t => t.avgValue || t.avgLikes),
                            backgroundColor: '#fbbf24',
                            borderRadius: 4
                          }]
                        }} 
                        options={{ responsive: true, maintainAspectRatio: false }} 
                      />
                    </div>
                  </div>

                  {/* Viral Chart 2: Theme Performance */}
                  <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                    <h3 className="text-base font-bold text-gray-900 dark:text-slate-100 flex items-center mb-6">
                      <TrendingUp className="w-5 h-5 mr-2 text-emerald-500" />
                      {isSearchEngine ? '各业务维度收录流量盘' : '各业务维度互动天花板'}
                    </h3>
                    <div className="h-[300px]">
                      <Bar 
                        data={{
                          labels: data.viralData.themePerformance.map(t => t.theme),
                          datasets: [{
                            label: currentMetricLabel,
                            data: data.viralData.themePerformance.map(t => t.avgValue || t.avgLikes),
                            backgroundColor: '#10b981',
                            borderRadius: 4
                          }]
                        }} 
                        options={{ responsive: true, maintainAspectRatio: false }} 
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm overflow-hidden">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-base font-bold text-gray-900 dark:text-slate-100 flex items-center">
                      <Sparkles className="w-5 h-5 mr-2 text-purple-500" />
                      {isSearchEngine ? `高位霸屏内容库 (按${currentMetricLabel.replace('平均', '').replace('数','')}排行)` : `高互爆款内容库 (按真实${currentMetricLabel.replace('平均', '')}排行)`}
                    </h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-slate-700 text-sm text-slate-500 bg-slate-50/50 dark:bg-slate-800/30">
                          <th className="py-3 px-4 font-medium w-16">排行</th>
                          <th className="py-3 px-4 font-medium">标题</th>
                          <th className="py-3 px-4 font-medium w-32">达人/媒体</th>
                          <th className="py-3 px-4 font-medium w-28">{currentMetricLabel.replace('平均', '')}</th>
                          <th className="py-3 px-4 font-medium w-28">内容类型</th>
                          <th className="py-3 px-4 font-medium w-32 text-center">操作</th>
                        </tr>
                      </thead>
                      <tbody className="text-sm">
                        {data.viralData.topViral.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                            <td className="py-4 px-4 font-bold text-slate-400">{idx + 1}</td>
                            <td className="py-4 px-4 font-medium text-gray-900 dark:text-slate-100">{item.title}</td>
                            <td className="py-4 px-4 text-slate-600 dark:text-slate-300">{item.media}</td>
                            <td className="py-4 px-4 font-bold text-rose-500">{isSearchEngine ? (item.score || 0) : ((item[viralMetric]||0) > 10000 ? ((item[viralMetric]||0)/10000).toFixed(1) + 'w' : (item[viralMetric]||0))}</td>
                            <td className="py-4 px-4 whitespace-nowrap"><span className="px-2 py-1 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded text-xs border border-amber-500/20 whitespace-nowrap">{item.type}</span></td>
                            <td className="py-4 px-4 text-center whitespace-nowrap min-w-[120px]">
                               <button 
                                  onClick={() => handleJumpToZhimo(item.title, item.type)}
                                  className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded shadow-sm text-xs font-medium whitespace-nowrap flex-shrink-0"
                               >
                                  <span className="whitespace-nowrap flex-shrink-0">智墨仿写</span>
                               </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
            <div className={`transition-all duration-300 ${loading ? 'opacity-50 pointer-events-none blur-[2px]' : ''}`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              
              {/* Chart 1: Media/KOL Leaderboard */}
              <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-base font-bold text-gray-900 dark:text-slate-100 flex items-center">
                    <BarChart2 className="w-5 h-5 mr-2 text-[#2996cf]" />
                    {platform === '百度' || platform === '知乎' || platform === '微博' ? '高排名媒体能见度排行' : '高排名达人能见度排行'}
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="text-xs text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                      算法: 倒数加权叠加
                    </div>
                    <button onClick={() => setIsLeaderboardModalOpen(true)} className="text-xs font-medium text-[#2996cf] hover:text-sky-600 transition-colors flex items-center">
                      查看完整榜单 <ChevronRight className="w-3 h-3 ml-0.5" />
                    </button>
                  </div>
                </div>
                <div className="h-[300px]">
                  <Bar 
                    data={{
                      labels: (data.leaderboard.labels || []).slice(0, 10),
                      datasets: [{
                        label: '能见度得分',
                        data: (data.leaderboard.data || []).slice(0, 10),
                        backgroundColor: '#2996cf',
                        borderRadius: 4
                      }]
                    }} 
                    options={barOptions} 
                  />
                </div>
              </div>

              {/* Chart 2: Content Type Analysis */}
              <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 dark:text-slate-100 flex items-center mb-6">
                  <PieChart className="w-5 h-5 mr-2 text-indigo-500" />
                  高排名内容类型分布 (内容策略指南)
                </h3>
                <div className="h-[300px]">
                  <Doughnut 
                    data={{
                      labels: data.contentTypes.labels,
                      datasets: [{
                        data: data.contentTypes.data,
                        backgroundColor: [
                          '#4b75ab', 
                          '#afcaa6', 
                          '#da9f31', 
                          '#ac9ac2', 
                          '#b7d1e6', 
                          '#334e4c', 
                          '#692f80', 
                          '#f7f0c5'  
                        ],
                        borderWidth: 0
                      }]
                    }} 
                    options={pieOptions} 
                  />
                </div>
              </div>
            </div>

            {/* Detailed Table Row */}
            <div className="bg-white dark:bg-slate-800/50 backdrop-blur-xl border border-gray-200 dark:border-slate-700/50 rounded-xl p-6 shadow-sm overflow-hidden">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-base font-bold text-gray-900 dark:text-slate-100 flex items-center">
                  <Search className="w-5 h-5 mr-2 text-emerald-500" />
                  高位排名溯源明细 & 一键内容生成
                </h3>
                <button className="flex items-center text-sm text-[#2996cf] bg-sky-50 dark:bg-sky-900/30 px-3 py-1.5 rounded-md hover:bg-sky-100 dark:hover:bg-sky-900/50 transition-colors">
                  <Filter className="w-4 h-4 mr-1" />
                  高级筛选
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-slate-700 text-sm text-slate-500 bg-slate-50/50 dark:bg-slate-800/30">
                      <th className="py-3 px-4 font-medium w-16 whitespace-nowrap">排名</th>
                      <th className="py-3 px-4 font-medium w-32 whitespace-nowrap">触发关键词</th>
                      <th className="py-3 px-4 font-medium min-w-[300px]">页面标题 / 笔记内容</th>
                      <th className="py-3 px-4 font-medium w-32 whitespace-nowrap">{platform === '百度' || platform === '知乎' || platform === '微博' ? '域名属地' : '达人账号'}</th>
                      <th className="py-3 px-4 font-medium w-32 whitespace-nowrap">单条能见度估值</th>
                      <th className="py-3 px-4 font-medium w-28 whitespace-nowrap">识别类型</th>
                      <th className="py-3 px-4 font-medium w-36 text-center whitespace-nowrap">操作与对策</th>
                    </tr>
                  </thead>
                  <tbody className="text-sm">
                    {data.evidence.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100 dark:border-slate-700/50 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md font-bold text-xs ${
                            item.rank === 1 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400' :
                            item.rank === 2 ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300' :
                            item.rank === 3 ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-400' :
                            'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                          }`}>
                            {item.rank}
                          </span>
                        </td>
                        <td className="py-4 px-4 font-medium text-slate-700 dark:text-slate-300 whitespace-nowrap">
                          <div className="flex items-center gap-1.5">
                            <Search className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <span>{item.keyword}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 font-medium text-gray-900 dark:text-slate-100">
                          {item.title}
                        </td>
                        <td className="py-4 px-4">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 max-w-[130px] overflow-hidden whitespace-nowrap text-ellipsis" title={item.media}>
                            {platform === '百度' || platform === '知乎' || platform === '微博' ? <LinkIcon className="w-3.5 h-3.5 flex-shrink-0" /> : <UserCheck className="w-3.5 h-3.5 flex-shrink-0" />}
                            <span className="truncate">{item.media}</span>
                          </span>
                        </td>
                        <td className="py-4 px-4 text-center">
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5 max-w-[60px]">
                              <div className="bg-[#2996cf] h-1.5 rounded-full" style={{ width: `${item.score}%` }}></div>
                            </div>
                            <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">{item.score}</span>
                          </div>
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap">
                          <span className="px-2 py-1 bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded text-xs border border-indigo-500/20 whitespace-nowrap">
                            {item.type}
                          </span>
                        </td>
                        <td className="py-4 px-4 whitespace-nowrap w-36">
                          <div className="flex items-center justify-center gap-2 flex-nowrap">
                            <button onClick={() => window.open(item.url !== '#' && item.url ? item.url : 'javascript:void(0)', '_blank')} className="p-1.5 text-slate-400 flex-shrink-0 hover:text-[#2996cf] bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded shadow-sm transition-colors" title="查看原始页面">
                              <ExternalLink className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleJumpToZhimo(item.title, item.type)}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white rounded shadow-sm text-xs font-medium transition-all whitespace-nowrap flex-shrink-0"
                            >
                              <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                              <span className="whitespace-nowrap flex-shrink-0">智墨一键仿写</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
            )}
          </>
        )}
      </div>

      {isLeaderboardModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-gray-200 dark:border-slate-700">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-[#2996cf]" />
                {platform === '百度' || platform === '知乎' || platform === '微博' ? '高排名媒体能见度排行' : '高排名达人能见度排行'} - 完整榜单
              </h3>
              <button onClick={() => setIsLeaderboardModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700 text-sm text-slate-500 bg-slate-50/50 dark:bg-slate-800/30">
                    <th className="py-3 px-4 font-medium w-16">排名</th>
                    <th className="py-3 px-4 font-medium">{platform === '百度' || platform === '知乎' || platform === '微博' ? '域名属地' : '达人账号'}</th>
                    <th className="py-3 px-4 font-medium w-32 text-right">能见度得分</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {data.leaderboard.labels.map((label, idx) => (
                    <tr key={idx} className="border-b border-gray-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md font-bold text-xs ${
                          idx === 0 ? 'bg-amber-100 text-amber-700' :
                          idx === 1 ? 'bg-slate-200 text-slate-700' :
                          idx === 2 ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {idx + 1}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium text-slate-700 dark:text-slate-300">{label}</td>
                      <td className="py-3 px-4 text-right font-semibold text-[#2996cf]">{data.leaderboard.data[idx]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
